import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getAllInvoices, generateInvoiceBatch, confirmPayment, cancelInvoice,
  getAllPlans, createPlan, updatePlan, deletePlan,
  getTenants, getMasterSettings, updateMasterSettings,
  getPendingReviewInvoices, reviewInvoiceProof,
  testAsaasConnection,
} from '../../services/api';
import Toggle from '../../components/Toggle';
import MoneyInput from '../../components/MoneyInput';
import { FeaturesEditor, DEFAULT_FEATURES } from './MasterTenantForm';
import { safeParseFeatures } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';

const S_BADGE = { pending:'badge-warning', paid:'badge-success', overdue:'badge-danger', cancelled:'badge-gray' };
const S_LABEL = { pending:'Pendente', paid:'Pago', overdue:'Vencido', cancelled:'Cancelado' };
const fmt = n => `R$ ${parseFloat(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

// Safe date formatter: handles YYYY-MM-DD strings AND ISO strings/Date objects from DB
const fmtDate = (d) => {
  if (!d) return '';
  const s = typeof d === 'string' && d.length > 10 ? d.slice(0, 10) : typeof d === 'string' ? d : new Date(d).toISOString().slice(0, 10);
  return new Date(s + 'T12:00:00').toLocaleDateString('pt-BR');
};

export default function MasterBilling() {
  const confirm = useConfirm();
  const [invoices,    setInvoices]    = useState([]);
  const [tenants,     setTenants]     = useState([]);
  const [plans,       setPlans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [statusF,     setStatusF]     = useState('');
  const [tenantF,     setTenantF]     = useState('');
  const [acting,      setActing]      = useState({});
  const [pixModal,    setPixModal]    = useState(null);

  // Batch modal
  const [batchModal,  setBatchModal]  = useState(false);
  const [batch,       setBatch]       = useState({ tenant_id:'', plan_ids:[], start_month:'', end_month:'' });
  const [batchLoading,setBatchLoading]= useState(false);

  // Plan management
  const [planModal,   setPlanModal]   = useState(false);
  const [planEdit,    setPlanEdit]    = useState(null);
  const [planForm,    setPlanForm]    = useState({ name:'', price:'', price_annual:'', max_users:30, features:{ ...DEFAULT_FEATURES } });
  const [planSaving,  setPlanSaving]  = useState(false);
  const [planActing,  setPlanActing]  = useState({});
  const [planTab,     setPlanTab]     = useState('basic'); // 'basic' | 'features'

  // Comprovantes aguardando revisão (Fase B)
  const [pendingProofs,   setPendingProofs]   = useState([]);
  const [pendingLoaded,   setPendingLoaded]   = useState(false);
  const [proofReviewModal, setProofReviewModal] = useState(null); // invoice
  const [proofReviewNotes, setProofReviewNotes] = useState('');
  const [proofReviewActing, setProofReviewActing] = useState(false);

  // PIX settings
  const [pixSection,       setPixSection]       = useState(false);
  const [pixCode,          setPixCode]          = useState('');
  const [pixKeyType,       setPixKeyType]       = useState('email');
  const [pixKey,           setPixKey]           = useState('');
  const [webhookSecret,    setWebhookSecret]    = useState('');
  const [webhookSecretMasked, setWebhookSecretMasked] = useState(null); // display-only
  const [webhookSecretConfigured, setWebhookSecretConfigured] = useState(false);
  const [pixSaving,        setPixSaving]        = useState(false);
  const [pixLoaded,        setPixLoaded]        = useState(false);
  const webhookUrl = `${window.location.origin}/api/webhooks/pix`;

  // Asaas (Fase C) — toggle + API key + environment + teste de conexão
  const [pixProvider,      setPixProvider]      = useState('manual');
  const [asaasApiKey,      setAsaasApiKey]      = useState(''); // input livre (nunca mostra o real masked)
  const [asaasApiKeyMasked, setAsaasApiKeyMasked] = useState(null); // só pra display
  const [asaasApiKeyConfigured, setAsaasApiKeyConfigured] = useState(false);
  const [asaasEnv,         setAsaasEnv]         = useState('sandbox');
  const [asaasLastTestOk,  setAsaasLastTestOk]  = useState(null);
  const [asaasLastTestAt,  setAsaasLastTestAt]  = useState(null);
  const [asaasTesting,     setAsaasTesting]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getAllInvoices({ status: statusF, tenant_id: tenantF }),
      getTenants(),
      getAllPlans(),
    ]).then(([inv, ten, pln]) => {
      setInvoices(inv.data.invoices);
      setTenants(ten.data.tenants);
      setPlans(pln.data.plans);
    }).catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [statusF, tenantF]);

  useEffect(() => { load(); }, [load]);

  const loadPendingProofs = useCallback(() => {
    getPendingReviewInvoices()
      .then(r => { setPendingProofs(r.data.invoices || []); setPendingLoaded(true); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadPendingProofs(); }, [loadPendingProofs]);

  const openProofReview = (invoice) => {
    setProofReviewModal(invoice);
    setProofReviewNotes('');
  };

  const handleProofReview = async (action) => {
    if (!proofReviewModal) return;
    setProofReviewActing(true);
    try {
      await reviewInvoiceProof(proofReviewModal.id, action, proofReviewNotes);
      toast.success(action === 'confirm' ? 'Pagamento confirmado!' : 'Comprovante rejeitado');
      setProofReviewModal(null);
      setProofReviewNotes('');
      loadPendingProofs();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao processar revisão');
    } finally {
      setProofReviewActing(false);
    }
  };

  const loadPixSettings = () => {
    if (pixLoaded) return;
    getMasterSettings().then(r => {
      const s = r.data.settings || {};
      setPixCode(s.pix_code || '');
      setPixKeyType(s.pix_key_type || 'email');
      setPixKey(s.pix_key || '');
      // webhook_pix_secret vem mascarado do backend (****últimos4). Separamos
      // em "masked (display)" e "novo valor" (input vazio até o master digitar).
      setWebhookSecretMasked(s.webhook_pix_secret || null);
      setWebhookSecretConfigured(Boolean(s.webhook_pix_secret_configured));
      setWebhookSecret('');
      // Asaas (Fase C)
      setPixProvider(s.pix_provider || 'manual');
      setAsaasApiKeyMasked(s.asaas_api_key || null); // já vem mascarado do backend
      setAsaasApiKeyConfigured(Boolean(s.asaas_api_key_configured));
      setAsaasApiKey(''); // campo livre inicia vazio
      setAsaasEnv(s.asaas_environment || 'sandbox');
      setAsaasLastTestOk(s.asaas_last_test_ok === '1' ? true : s.asaas_last_test_ok === '0' ? false : null);
      setAsaasLastTestAt(s.asaas_last_test_at || null);
      setPixLoaded(true);
    }).catch(() => {});
  };

  const handleAsaasTest = async () => {
    setAsaasTesting(true);
    try {
      // Se o input tem texto, testa com ele; senão usa o que já está salvo
      const body = {};
      if (asaasApiKey.trim()) body.apiKey = asaasApiKey.trim();
      if (asaasEnv) body.environment = asaasEnv;
      const r = await testAsaasConnection(body);
      toast.success(r.data.message || 'Conexão OK');
      setAsaasLastTestOk(true);
      setAsaasLastTestAt(new Date().toISOString());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao testar Asaas');
      setAsaasLastTestOk(false);
      setAsaasLastTestAt(new Date().toISOString());
    } finally {
      setAsaasTesting(false);
    }
  };

  const togglePixSection = () => {
    if (!pixSection) loadPixSettings();
    setPixSection(s => !s);
  };

  const handlePixSave = async () => {
    if (!pixKey.trim() && !pixCode.trim()) return toast.error('Informe a chave PIX ou o código copia-e-cola');
    setPixSaving(true);
    try {
      // Carrega as settings atuais para não sobrescrever configurações
      // não-PIX (ex.: custo WhatsApp — gerenciadas em /master/system).
      const current = (await getMasterSettings()).data.settings || {};
      const payload = {
        ...current,
        pix_key_type: pixKeyType,
        pix_key: pixKey.trim(),
        pix_code: pixCode.trim(),
        // Asaas (Fase C)
        pix_provider: pixProvider,
        asaas_environment: asaasEnv,
      };
      // webhook_pix_secret: só envia se master digitou nova. Caso contrário
      // mantém o que está salvo (o backend ignora valores "****" por segurança,
      // então remover do payload deixa o valor real intacto).
      if (webhookSecret.trim()) {
        payload.webhook_pix_secret = webhookSecret.trim();
      } else {
        delete payload.webhook_pix_secret;
        delete payload.webhook_pix_secret_configured;
      }
      // Só envia asaas_api_key se o master digitou uma nova — se o campo
      // estiver vazio e já havia chave salva, preservamos a antiga (o backend
      // ignora valores mascarados que começam com "****").
      if (asaasApiKey.trim()) {
        payload.asaas_api_key = asaasApiKey.trim();
      } else {
        // Remove a versão mascarada de current pra não sobrescrever
        delete payload.asaas_api_key;
        delete payload.asaas_api_key_configured;
      }
      await updateMasterSettings({ settings: payload });
      toast.success('Configuração PIX salva!');
      // Atualiza estado local sem precisar recarregar
      if (asaasApiKey.trim()) {
        setAsaasApiKeyConfigured(true);
        setAsaasApiKey('');
      }
    } catch { toast.error('Erro ao salvar'); }
    finally { setPixSaving(false); }
  };

  // ── Batch generation ──────────────────────────────────────────────────────
  // Tenant selecionado no batch — usado pra pré-preencher plano+preço do contrato
  const selectedTenant = tenants.find(t => t.id === batch.tenant_id) || null;

  // Quando master escolhe um tenant, pré-marca o plano contratado (subscribed_plan_id)
  // caso exista, pra evitar preencher do zero. Master ainda pode adicionar outros
  // planos como "extras" via toggle.
  useEffect(() => {
    if (!batch.tenant_id || !selectedTenant) return;
    const contractPlanId = selectedTenant.subscribed_plan_id || selectedTenant.plan_id;
    if (contractPlanId && batch.plan_ids.length === 0) {
      setBatch(b => ({ ...b, plan_ids: [contractPlanId] }));
    }
    // Se batch.plan_ids já foi editado pelo master, não sobrescrevemos.
  }, [batch.tenant_id, selectedTenant, batch.plan_ids.length]);

  const togglePlan = id => setBatch(b => ({
    ...b,
    plan_ids: b.plan_ids.includes(id) ? b.plan_ids.filter(x => x !== id) : [...b.plan_ids, id],
  }));

  // Período de cobrança do tenant selecionado: 'monthly' (default) ou 'annual'.
  // Determina qual preço mostrar nos cards e usar no total da fatura.
  const billingPeriod = selectedTenant?.billing_period === 'annual' ? 'annual' : 'monthly';
  const isAnnual = billingPeriod === 'annual';
  const periodLabel = isAnnual ? '/ano' : '/mês';
  const periodUnit  = isAnnual ? 'ano' : 'mês';

  // Preço efetivo por plano considerando (a) período contratado e (b) contrato
  // travado (subscribed_price) no caso mensal.
  //
  // REGRA CRÍTICA de cobrança:
  //  - ANUAL: usa sempre plan.prices.annual.amount (nunca subscribed_price, que
  //    é mensal). Campanha não aplica em anual por design.
  //  - MENSAL: usa subscribed_price se (i) o plano for o contratado e (ii) o
  //    desconto ainda está travado (campaign_locked_until no futuro/NULL).
  //    Caso contrário usa plan.prices.monthly.amount.
  const effectivePlanPrice = (plan) => {
    // plan.prices pode vir como { monthly: {amount, currency}, annual: {...} }
    // ou ser ausente se o backend não retornou ainda (fallback pra plan.price).
    const monthlyP = plan.prices?.monthly?.amount ?? parseFloat(plan.price) ?? 0;
    const annualP  = plan.prices?.annual?.amount  ?? (parseFloat(plan.price) * 10) ?? 0;

    if (isAnnual) return parseFloat(annualP);

    // Mensal: pode aplicar preço de contrato travado
    if (!selectedTenant) return parseFloat(monthlyP);
    const contractPlanId = selectedTenant.subscribed_plan_id;
    const contractPrice  = selectedTenant.subscribed_price;
    if (plan.id !== contractPlanId || contractPrice == null) return parseFloat(monthlyP);
    const lockedUntil = selectedTenant.campaign_locked_until;
    if (lockedUntil && String(lockedUntil) !== 'null') {
      const today = new Date().toISOString().slice(0, 10);
      if (today > String(lockedUntil).slice(0, 10)) return parseFloat(monthlyP);
    }
    return parseFloat(contractPrice);
  };

  const batchTotal = plans.filter(p => batch.plan_ids.includes(p.id))
    .reduce((a, p) => a + effectivePlanPrice(p), 0);

  // Número de faturas que serão geradas no range selecionado.
  // Mensal: (mesesFim - mesesIni + 1)
  // Anual:  (anoFim - anoIni + 1) — 1 fatura por ano
  const batchMonths = () => {
    if (!batch.start_month || !batch.end_month) return 0;
    const [sy, sm] = batch.start_month.split('-').map(Number);
    const [ey, em] = batch.end_month.split('-').map(Number);
    if (isAnnual) {
      // ignora o mês, conta anos
      if (ey < sy) return 0;
      return (ey - sy) + 1;
    }
    return Math.max(0, (ey - sy) * 12 + (em - sm) + 1);
  };

  const handleBatch = async e => {
    e.preventDefault();
    if (!batch.tenant_id) return toast.error('Selecione o cliente');
    if (!batch.plan_ids.length) return toast.error('Selecione ao menos um plano');
    if (!batch.start_month || !batch.end_month) return toast.error('Defina o período');
    setBatchLoading(true);
    try {
      const r = await generateInvoiceBatch(batch);
      toast.success(`${r.data.generated} fatura(s) gerada(s)!`);
      setBatchModal(false);
      setBatch({ tenant_id:'', plan_ids:[], start_month:'', end_month:'' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao gerar faturas'); }
    finally { setBatchLoading(false); }
  };

  // ── Plan management ───────────────────────────────────────────────────────
  const openNewPlan  = () => {
    setPlanEdit(null);
    setPlanForm({ name:'', price:'', price_annual:'', max_users:30, features:{ ...DEFAULT_FEATURES } });
    setPlanTab('basic');
    setPlanModal(true);
  };
  const openEditPlan = p => {
    setPlanEdit(p);
    const feats = safeParseFeatures(p.features);
    // max_users pode ser 0 (=ilimitado). Não usar `|| 30` porque isso
    // converteria 0 para 30 e zeraria o conceito de ilimitado.
    const maxUsers = p.max_users != null ? p.max_users : 30;
    // Preço mensal: prefere prices.monthly (nova API) com fallback para
    // p.price (legado). Preço anual: prefere prices.annual; se não vier,
    // mostra vazio pra o master preencher — não inventa valores.
    const monthlyPrice = p.prices?.monthly?.amount != null ? p.prices.monthly.amount : p.price;
    const annualPrice  = p.prices?.annual?.amount  != null ? p.prices.annual.amount  : '';
    setPlanForm({
      name: p.name,
      price: monthlyPrice,
      price_annual: annualPrice,
      max_users: maxUsers,
      features: { ...DEFAULT_FEATURES, ...feats },
    });
    setPlanTab('basic');
    setPlanModal(true);
  };

  const handlePlanSave = async e => {
    e.preventDefault();
    if (!planForm.name.trim() || planForm.price === '') return toast.error('Nome e preço são obrigatórios');
    setPlanSaving(true);
    try {
      // max_users=0 é ilimitado — tratar string vazia como 0 (preserve o "0 = ilimitado").
      const parsedMaxUsers = planForm.max_users === '' || planForm.max_users == null
        ? 30
        : parseInt(planForm.max_users, 10);
      const monthly = parseFloat(planForm.price);
      // Preço anual: opcional. Se vazio, o backend usa fallback monthly*10.
      const annualRaw = planForm.price_annual;
      const annual = (annualRaw === '' || annualRaw == null) ? null : parseFloat(annualRaw);
      if (annual != null && !(annual >= 0)) return toast.error('Preço anual inválido');
      const payload = {
        name: planForm.name,
        price: monthly,
        max_users: Number.isNaN(parsedMaxUsers) ? 30 : parsedMaxUsers,
        features: planForm.features,
        prices: {
          monthly,
          ...(annual != null ? { annual } : {}),
        },
      };
      if (planEdit) {
        await updatePlan(planEdit.id, payload);
        toast.success('Plano atualizado! A landing reflete em até 60s (cache).');
      } else {
        await createPlan(payload);
        toast.success('Plano criado!');
      }
      setPlanModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
    finally { setPlanSaving(false); }
  };

  const handlePlanDelete = async p => {
    const ok = await confirm({
      title: `Desativar plano "${p.name}"?`,
      message: 'O plano fica marcado como inativo — não aparece mais para novos clientes mas faturas e assinaturas antigas continuam válidas. Para reativar depois, edite o plano.',
      confirmText: 'Desativar',
      variant: 'warning',
    });
    if (!ok) return;
    setPlanActing(a => ({ ...a, [p.id]: true }));
    try {
      await deletePlan(p.id);
      toast.success('Plano desativado!');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
    finally { setPlanActing(a => ({ ...a, [p.id]: false })); }
  };

  // ── Invoice actions ───────────────────────────────────────────────────────
  const withActing = (key, fn) => async () => {
    setActing(p => ({ ...p, [key]: true }));
    try { await fn(); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
    finally { setActing(p => ({ ...p, [key]: false })); }
  };

  const totalPending = invoices.filter(i => i.status === 'pending').reduce((a, i) => a + parseFloat(i.amount), 0);
  const totalPaid    = invoices.filter(i => i.status === 'paid').reduce((a, i) => a + parseFloat(i.amount), 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((a, i) => a + parseFloat(i.amount), 0);

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header"><h2>Faturamento Global</h2><p>Faturas de todos os clientes</p></div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm" onClick={togglePixSection}>
            {pixSection ? '▲ Fechar PIX' : '🔑 Config PIX'}
          </button>
          <button className="btn btn-primary" onClick={() => setBatchModal(true)}>📄 Gerar Faturas</button>
        </div>
      </div>

      {/* Alerta de comprovantes aguardando revisão (Fase B do PIX automático) */}
      {pendingProofs.length > 0 && (
        <div className="warning-box" style={{ marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div>
            <strong style={{ fontSize:14 }}>📎 {pendingProofs.length} comprovante{pendingProofs.length === 1 ? '' : 's'} aguardando revisão</strong>
            <div style={{ fontSize:12, marginTop:2, opacity:0.85 }}>
              Clientes enviaram comprovantes PIX que precisam do seu double-check antes de confirmar a fatura.
            </div>
          </div>
        </div>
      )}
      {pendingProofs.length > 0 && (
        <div className="card" style={{ marginBottom:20, borderLeft:'3px solid var(--warning)' }}>
          <div className="card-header">
            <span className="card-title">📎 Comprovantes aguardando revisão ({pendingProofs.length})</span>
          </div>
          <div className="card-body" style={{ padding:0 }}>
            {pendingProofs.map(p => {
              const extracted = (() => {
                try { return typeof p.proof_extracted_data === 'string' ? JSON.parse(p.proof_extracted_data) : p.proof_extracted_data; }
                catch { return null; }
              })();
              const flags = (() => {
                try { return typeof p.proof_flags === 'string' ? JSON.parse(p.proof_flags) : p.proof_flags; }
                catch { return []; }
              })();
              return (
                <div key={p.id} style={{ padding:'14px 18px', borderBottom:'1px solid var(--gray-100)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{p.tenant_name} · Fatura #{p.id}</div>
                    <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
                      Esperado {fmt(p.amount)}
                      {extracted?.valor != null && extracted.valor !== parseFloat(p.amount) && (
                        <> · Comprovante: <strong style={{ color:'var(--danger)' }}>{fmt(extracted.valor)}</strong></>
                      )}
                      {extracted?.pagador && <> · Pagador: {extracted.pagador.slice(0, 40)}</>}
                    </div>
                    {flags?.length > 0 && (
                      <div style={{ fontSize:11, color:'var(--warning)', marginTop:4 }}>
                        ⚠️ {flags.join(' · ')}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={() => openProofReview(p)}>
                    Revisar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PIX Settings */}
      {pixSection && (
        <div className="card" style={{ marginBottom:20, borderLeft:'3px solid var(--primary)' }}>
          <div className="card-header"><span className="card-title">🔑 Configuração PIX Global</span></div>
          <div className="card-body">
            <p style={{ fontSize:13, color:'var(--gray-500)', marginBottom:16 }}>
              Estas informações são usadas automaticamente em todas as faturas geradas. Configure sua chave PIX para recebimento.
            </p>

            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Chave PIX *</label>
                <select value={pixKeyType} onChange={e => setPixKeyType(e.target.value)}>
                  <option value="email">E-mail</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="phone">Telefone</option>
                  <option value="random">Chave Aleatória (EVP)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Chave PIX *</label>
                <input value={pixKey} onChange={e => setPixKey(e.target.value)}
                  placeholder={
                    pixKeyType === 'email' ? 'pagamentos@suaempresa.com' :
                    pixKeyType === 'cpf'   ? '000.000.000-00' :
                    pixKeyType === 'cnpj'  ? '00.000.000/0001-00' :
                    pixKeyType === 'phone' ? '+5511999999999' :
                    'chave-aleatória-aqui'
                  } />
                <small>Chave usada quando o inquilino abrir a fatura para pagar.</small>
              </div>
            </div>

            <div className="form-group" style={{ marginTop:4 }}>
              <label>Código PIX Copia-e-Cola <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(opcional)</span></label>
              <textarea value={pixCode} onChange={e => setPixCode(e.target.value)}
                rows={3} placeholder="00020126... (código completo — gere no app do banco)" style={{ fontFamily:'monospace', fontSize:12 }} />
              <small>Se preenchido, o código copia-e-cola tem prioridade. Deixe em branco para usar somente a chave acima.</small>
            </div>

            <div className="form-group" style={{ marginTop:4 }}>
              <label>Token do Webhook PIX <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(confirmação automática)</span></label>
              <input
                type="password"
                value={webhookSecret}
                onChange={e => setWebhookSecret(e.target.value)}
                placeholder={webhookSecretConfigured ? `Configurado (${webhookSecretMasked || '****'}) — digite novo pra trocar` : 'token-secreto-gerado-por-voce'}
                autoComplete="off"
              />
              <small>Configure o mesmo token no seu gateway PIX (Gerencianet, Asaas, etc.) no campo "Webhook Secret" ou "X-Webhook-Token". O valor fica ocultado após salvar — nunca é retornado bruto.</small>
            </div>
            {(webhookSecret.trim() || webhookSecretConfigured) && (
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:12, fontWeight:600, color:'var(--gray-700)', display:'block', marginBottom:4 }}>URL do Webhook (configure no seu banco/gateway)</label>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <code style={{ background:'var(--gray-100)', padding:'8px 12px', borderRadius:6, fontSize:11, fontFamily:'monospace', wordBreak:'break-all', flex:1 }}>{webhookUrl}</code>
                  <button className="btn btn-secondary btn-sm" style={{ flexShrink:0 }}
                    onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('URL copiada!'); }}>
                    Copiar
                  </button>
                </div>
              </div>
            )}
            <div style={{ marginTop: 8, marginBottom: 16, fontSize: 12, color: 'var(--gray-500)' }}>
              💡 Custo do WhatsApp e diagnóstico de e-mail foram movidos para <strong>Sistema</strong> no menu lateral.
            </div>

            {/* Automação PIX (Asaas) — Fase C */}
            <div style={{
              marginTop:20, padding:'16px', borderRadius:10,
              background:'var(--gray-50)', border:'1px solid var(--gray-200)',
            }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, flexWrap:'wrap', gap:8 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14 }}>🤖 Reconciliação automática (Asaas)</div>
                  <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
                    Alternativa ao fluxo manual: Asaas gera QR PIX dinâmico + notifica pagamento via webhook (R$ 1,99/transação, aceita CPF).
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:12, color:'var(--gray-600)' }}>Provider:</span>
                  <select value={pixProvider} onChange={e => setPixProvider(e.target.value)} style={{ minWidth:120 }}>
                    <option value="manual">Manual + comprovante</option>
                    <option value="asaas">Asaas (automático)</option>
                  </select>
                </div>
              </div>

              {pixProvider === 'asaas' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ambiente</label>
                      <select value={asaasEnv} onChange={e => setAsaasEnv(e.target.value)}>
                        <option value="sandbox">Sandbox (testes)</option>
                        <option value="production">Produção</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>API Key</label>
                      <input
                        type="password"
                        value={asaasApiKey}
                        onChange={e => setAsaasApiKey(e.target.value)}
                        placeholder={asaasApiKeyConfigured ? `Configurada (${asaasApiKeyMasked || '****'}) — digite nova pra trocar` : 'Cole sua API key do Asaas'}
                        autoComplete="off"
                      />
                      <small>Obtida em app.asaas.com → Integrações → API. Nunca é retornada bruta pelo backend.</small>
                    </div>
                  </div>

                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginTop:8 }}>
                    <button
                      type="button"
                      className={`btn btn-sm btn-secondary${asaasTesting ? ' btn-loading' : ''}`}
                      onClick={handleAsaasTest}
                      disabled={asaasTesting || (!asaasApiKeyConfigured && !asaasApiKey.trim())}
                    >
                      {asaasTesting ? 'Testando...' : '↻ Testar conexão'}
                    </button>
                    {asaasLastTestAt && (
                      <span style={{ fontSize:12, color: asaasLastTestOk ? 'var(--success)' : 'var(--danger)' }}>
                        {asaasLastTestOk ? '✓ Conectado' : '✗ Falhou'} · {new Date(asaasLastTestAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop:10, fontSize:11, color:'var(--gray-500)', lineHeight:1.5 }}>
                    ⚠️ Cada tenant precisa ter <strong>CPF/CNPJ</strong> cadastrado pra faturas via Asaas. Tenants sem CPF caem automaticamente no fluxo manual (fallback gracioso — nunca bloqueia geração).
                    <br />
                    💡 O webhook do Asaas deve apontar pra <code>{webhookUrl}</code> com header <code>X-Webhook-Token: &lt;webhook_pix_secret&gt;</code>.
                  </div>
                </>
              )}
            </div>

            <button className={`btn btn-primary${pixSaving?' btn-loading':''}`}
              style={{ marginTop:16 }}
              disabled={pixSaving} onClick={handlePixSave}>
              {pixSaving ? 'Salvando...' : '💾 Salvar configuração PIX'}
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        {[
          { icon:'⏳', label:'A Receber', value: fmt(totalPending), color:'amber' },
          { icon:'✅', label:'Recebido',  value: fmt(totalPaid),    color:'green' },
          { icon:'⚠️', label:'Vencido',   value: fmt(totalOverdue), color:'red'   },
        ].map(c => (
          <div key={c.label} className="stat-card">
            <div className={`stat-icon ${c.color}`}>{c.icon}</div>
            <div><div className="stat-label">{c.label}</div><div className="stat-value" style={{ fontSize:17 }}>{c.value}</div></div>
          </div>
        ))}
      </div>

      {/* Plans */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span className="card-title">📦 Planos de Cobrança</span>
          <button className="btn btn-primary btn-sm" onClick={openNewPlan}>+ Novo Plano</button>
        </div>
        <div className="card-body" style={{ padding:0 }}>
          {plans.length === 0
            ? <div style={{ padding:20, textAlign:'center', color:'var(--gray-400)', fontSize:13 }}>Nenhum plano cadastrado</div>
            : plans.map(p => {
              const monthly = p.prices?.monthly?.amount != null ? p.prices.monthly.amount : p.price;
              const annual = p.prices?.annual?.amount != null ? p.prices.annual.amount : null;
              let annualPct = null;
              if (annual != null && monthly > 0) {
                const baseline = monthly * 12;
                annualPct = Math.round(Math.max(0, baseline - annual) / baseline * 100);
              }
              return (
              <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid var(--gray-100)', gap:12, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    {p.name}
                    {p.is_default && <span className="badge badge-info" style={{ fontSize:10 }} title="Plano padrão do sistema — não pode ser excluído e aparece na landing pública">⭐ padrão</span>}
                    {!p.active && <span className="badge badge-gray" style={{ fontSize:10 }}>inativo</span>}
                    {p.discount_active && parseFloat(p.discount_pct) > 0 && (
                      <span className="badge badge-warning" style={{ fontSize:10 }}>🔥 {p.discount_pct}% OFF</span>
                    )}
                  </div>
                  <div style={{ fontSize:13, color:'var(--gray-500)' }}>
                    {fmt(monthly)}/mês
                    {annual != null && (
                      <span style={{ marginLeft:10 }}>
                        • {fmt(annual)}/ano{annualPct ? <strong style={{ color:'var(--success)', marginLeft:4 }}>(-{annualPct}%)</strong> : null}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEditPlan(p)}>Editar</button>
                  {p.active && !p.is_default && (
                    <button className={`btn btn-danger btn-sm${planActing[p.id]?' btn-loading':''}`}
                      disabled={planActing[p.id]} onClick={() => handlePlanDelete(p)}>
                      {planActing[p.id] ? '' : 'Desativar'}
                    </button>
                  )}
                </div>
              </div>
              );
            })
          }
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <select value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="">Todos status</option>
          <option value="pending">Pendentes</option>
          <option value="paid">Pagas</option>
          <option value="overdue">Vencidas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <select value={tenantF} onChange={e => setTenantF(e.target.value)} style={{ maxWidth:200 }}>
          <option value="">Todos os clientes</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {(statusF || tenantF) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setStatusF(''); setTenantF(''); }}>✕ Limpar</button>
        )}
      </div>

      {/* Invoice list */}
      {loading ? <div className="spinner" style={{ margin:'40px auto', display:'block' }} /> : (
        invoices.length === 0
          ? <div className="empty-state"><div className="empty-icon">💰</div><h3>Nenhuma fatura</h3></div>
          : <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {invoices.map(inv => (
                <div key={inv.id} className="mobile-card" style={inv.status === 'overdue' ? { borderLeft:'3px solid var(--danger)' } : {}}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:11, color:'var(--gray-400)' }}>#{inv.id}</div>
                      <div style={{ fontWeight:700, fontSize:15, marginTop:2 }}>{inv.tenant_name}</div>
                      <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:1 }}>{inv.description || 'Fatura de serviço'}</div>
                      {inv.reference_month && <div style={{ fontSize:11, color:'var(--gray-400)' }}>Ref: {inv.reference_month}</div>}
                    </div>
                    <span className={`badge ${S_BADGE[inv.status]||'badge-gray'}`}>{S_LABEL[inv.status]||inv.status}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12 }}>
                    <div>
                      <div style={{ fontSize:20, fontWeight:800 }}>{fmt(inv.amount)}</div>
                      <div style={{ fontSize:12, color:'var(--gray-400)', marginTop:2 }}>
                        Vence: {fmtDate(inv.due_date)}
                        {inv.paid_at && <span style={{ marginLeft:8 }}>• Pago: {fmtDate(inv.paid_at)}</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      {inv.pix_code && inv.status !== 'paid' && inv.status !== 'cancelled' && (
                        <button className="btn btn-xs btn-secondary" onClick={() => setPixModal(inv)}>📋 PIX</button>
                      )}
                      {(inv.status === 'pending' || inv.status === 'overdue') && (
                        <button className={`btn btn-xs btn-success${acting[`confirm-${inv.id}`]?' btn-loading':''}`}
                          disabled={acting[`confirm-${inv.id}`]}
                          onClick={withActing(`confirm-${inv.id}`, async () => { await confirmPayment(inv.id); toast.success('Pagamento confirmado!'); })}>
                          {acting[`confirm-${inv.id}`] ? '' : '✓ Pago'}
                        </button>
                      )}
                      {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                        <button className={`btn btn-xs btn-danger${acting[`cancel-${inv.id}`]?' btn-loading':''}`}
                          disabled={acting[`cancel-${inv.id}`]}
                          onClick={withActing(`cancel-${inv.id}`, async () => {
                            const ok = await confirm({
                              title: `Cancelar fatura #${inv.id}?`,
                              message: `Valor: ${fmt(inv.amount)} · Cliente: ${inv.tenant_name}\n\nA fatura fica marcada como cancelada e sai do "A Receber". Esta ação não pode ser revertida.`,
                              confirmText: 'Cancelar fatura',
                              cancelText: 'Voltar',
                              variant: 'danger',
                            });
                            if (!ok) throw new Error('cancelled');
                            await cancelInvoice(inv.id); toast.success('Fatura cancelada');
                          })}>
                          {acting[`cancel-${inv.id}`] ? '' : '✕'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
      )}

      {/* ── Batch modal ── */}
      {batchModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setBatchModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>📄 Gerar Faturas em Lote</h3>
              <button className="modal-close" onClick={() => setBatchModal(false)}>✕</button>
            </div>
            <form onSubmit={handleBatch}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Cliente *</label>
                  <select value={batch.tenant_id} onChange={e => setBatch(b => ({ ...b, tenant_id: parseInt(e.target.value) || '', plan_ids: [] }))}>
                    <option value="">Selecione o cliente</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>

                {/* Cartão do contrato do tenant — pré-preenche plano+preço.
                    Mostra valor/período correto (anual ou mensal). */}
                {selectedTenant && selectedTenant.subscribed_plan_id && (
                  <div style={{
                    padding:'12px 14px', marginBottom:14, borderRadius:8,
                    background:'var(--primary-faint)', border:'1px solid var(--primary-light)',
                    fontSize:13,
                  }}>
                    <div style={{ fontWeight:700, color:'var(--primary-dark)', fontSize:12, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:4 }}>
                      📄 Contrato do cliente {isAnnual && '(anual)'}
                    </div>
                    {(() => {
                      const contractPlan = plans.find(p => p.id === selectedTenant.subscribed_plan_id);

                      // Helper: parse seguro da data (Date object, ISO string ou YYYY-MM-DD).
                      // Evita bug "Invalid Date" quando o campo vem NULL/''.
                      const parseLockDate = (raw) => {
                        if (!raw || String(raw) === 'null') return null;
                        const s = raw instanceof Date
                          ? raw.toISOString().slice(0, 10)
                          : String(raw).slice(0, 10);
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
                        const d = new Date(s + 'T12:00:00');
                        return Number.isNaN(d.getTime()) ? null : d;
                      };

                      const lockedDate = parseLockDate(selectedTenant.campaign_locked_until);
                      const today = new Date().toISOString().slice(0, 10);
                      const lockExpired = lockedDate && today > lockedDate.toISOString().slice(0, 10);

                      if (isAnnual) {
                        // Contrato anual — mostra preço anual da tabela plan_prices
                        const annualPrice = contractPlan?.prices?.annual?.amount
                          ?? (parseFloat(contractPlan?.price || 0) * 10);
                        const monthlyEquiv = annualPrice / 12;
                        return (
                          <>
                            <div>
                              Plano: <strong>{contractPlan?.name || '—'}</strong>
                              {' · '}
                              <strong>{fmt(annualPrice)}/ano</strong>
                              <span style={{ fontSize:11, color:'var(--gray-500)', marginLeft:6 }}>
                                (equivalente a {fmt(monthlyEquiv)}/mês)
                              </span>
                            </div>
                            <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>
                              Fatura anual única. Campanhas de desconto não se aplicam ao período anual.
                            </div>
                          </>
                        );
                      }

                      // Contrato mensal (comportamento anterior)
                      const hasDiscount = selectedTenant.subscribed_price != null
                        && contractPlan
                        && parseFloat(selectedTenant.subscribed_price) < parseFloat(contractPlan.price)
                        && !lockExpired;
                      return (
                        <>
                          <div>
                            Plano: <strong>{contractPlan?.name || '—'}</strong>
                            {' · '}
                            {hasDiscount ? (
                              <>
                                <span style={{ textDecoration:'line-through', color:'var(--gray-400)' }}>{fmt(contractPlan.price)}</span>
                                {' '}
                                <strong style={{ color:'var(--success)' }}>{fmt(selectedTenant.subscribed_price)}/mês</strong>
                              </>
                            ) : (
                              <strong>{fmt(selectedTenant.subscribed_price || contractPlan?.price)}/mês</strong>
                            )}
                          </div>
                          {lockedDate && !lockExpired && (
                            <div style={{ fontSize:12, color:'var(--gray-600)', marginTop:4 }}>
                              Desconto de campanha trava até <strong>{lockedDate.toLocaleDateString('pt-BR')}</strong>
                            </div>
                          )}
                          {lockedDate && lockExpired && (
                            <div style={{ fontSize:12, color:'var(--warning)', marginTop:4 }}>
                              ⚠️ Desconto expirou em {lockedDate.toLocaleDateString('pt-BR')} — faturas novas serão geradas no preço cheio.
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="form-group">
                  <label>Planos *</label>
                  <small style={{ display:'block', marginBottom:8, color:'var(--gray-500)' }}>
                    {selectedTenant?.subscribed_plan_id
                      ? 'Plano contratado já selecionado. Adicione outros planos só se quiser cobrar extras nesta fatura.'
                      : 'Selecione os planos a cobrar.'}
                  </small>
                  {plans.filter(p => p.active).length === 0
                    ? <p style={{ color:'var(--gray-400)', fontSize:13 }}>Nenhum plano ativo. Crie um plano primeiro.</p>
                    : plans.filter(p => p.active).map(p => {
                      const isContractPlan = selectedTenant?.subscribed_plan_id === p.id;
                      const effective = effectivePlanPrice(p);
                      // Preço base a ser riscado quando há desconto (monthly só)
                      const monthlyBase = p.prices?.monthly?.amount ?? parseFloat(p.price);
                      const hasDiscount = !isAnnual && isContractPlan && effective < parseFloat(monthlyBase);
                      return (
                        <div key={p.id}
                          onClick={() => togglePlan(p.id)}
                          style={{
                            display:'flex', alignItems:'center', gap:10, padding:'10px 12px', marginBottom:6,
                            borderRadius:8, border:'1.5px solid',
                            borderColor: batch.plan_ids.includes(p.id) ? 'var(--primary)' : 'var(--gray-200)',
                            background: batch.plan_ids.includes(p.id) ? 'rgba(37,99,235,0.06)' : 'transparent',
                            cursor:'pointer', transition:'all .15s', fontSize:14,
                          }}>
                          <Toggle checked={batch.plan_ids.includes(p.id)} onChange={() => {}} />
                          <span style={{ flex:1 }}>
                            {p.name}
                            {isContractPlan && <span style={{ marginLeft:8, fontSize:10, padding:'2px 6px', borderRadius:4, background:'var(--primary)', color:'white', fontWeight:700 }}>CONTRATO</span>}
                          </span>
                          <span style={{ color:'var(--gray-500)', fontWeight:600 }}>
                            {hasDiscount ? (
                              <>
                                <span style={{ textDecoration:'line-through', fontWeight:400, marginRight:6, color:'var(--gray-400)' }}>{fmt(monthlyBase)}</span>
                                <span style={{ color:'var(--success)' }}>{fmt(effective)}{periodLabel}</span>
                              </>
                            ) : (
                              <>{fmt(effective)}{periodLabel}</>
                            )}
                          </span>
                        </div>
                      );
                    })
                  }
                  {batch.plan_ids.length > 0 && (
                    <div style={{ marginTop:8, padding:'8px 12px', background:'var(--gray-50)', borderRadius:6, fontSize:13 }}>
                      Total por fatura: <strong>{fmt(batchTotal)}{periodLabel}</strong>
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{isAnnual ? 'Ano inicial *' : 'Mês inicial *'}</label>
                    <input type="month" value={batch.start_month} onChange={e => setBatch(b => ({ ...b, start_month: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>{isAnnual ? 'Ano final *' : 'Mês final *'}</label>
                    <input type="month" value={batch.end_month} onChange={e => setBatch(b => ({ ...b, end_month: e.target.value }))} />
                  </div>
                </div>

                {batchMonths() > 0 && batch.plan_ids.length > 0 && (
                  <div className="info-box" style={{ fontSize:13 }}>
                    <strong>{batchMonths()} fatura(s) {isAnnual ? 'anuais' : 'mensais'}</strong> com vencimento no <strong>5º dia útil</strong> de cada {periodUnit}.<br/>
                    Valor total gerado: <strong>{fmt(batchTotal * batchMonths())}</strong> · PIX global configurado automaticamente.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setBatchModal(false)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${batchLoading?' btn-loading':''}`} disabled={batchLoading}>
                  {batchLoading ? 'Gerando...' : `Gerar ${batchMonths() || ''} Fatura(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Plan modal ── */}
      {planModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPlanModal(false)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header">
              <h3>{planEdit ? `Editar Plano: ${planEdit.name}` : 'Novo Plano'}</h3>
              <button className="modal-close" onClick={() => setPlanModal(false)}>✕</button>
            </div>
            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:'2px solid var(--gray-100)', padding:'0 24px' }}>
              {[['basic','📋 Básico'],['features','⚙️ Features']].map(([tab,label]) => (
                <button key={tab} type="button" onClick={() => setPlanTab(tab)} style={{
                  padding:'10px 16px', border:'none', background:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                  color: planTab===tab ? 'var(--primary)' : 'var(--gray-500)',
                  borderBottom: planTab===tab ? '2px solid var(--primary)' : '2px solid transparent',
                  marginBottom:-2,
                }}>{label}</button>
              ))}
            </div>
            <form onSubmit={handlePlanSave}>
              <div className="modal-body" style={{ maxHeight:480, overflowY:'auto' }}>
                {planTab === 'basic' && (
                  <>
                    {planEdit && (
                      <div className="info-box" style={{ marginBottom:12, fontSize:12 }}>
                        Alterar o nome refletirá em todas as faturas em aberto vinculadas a este plano.
                      </div>
                    )}
                    <div className="form-group">
                      <label>Nome *</label>
                      <input value={planForm.name} onChange={e => setPlanForm(f => ({ ...f, name: e.target.value }))} autoFocus placeholder="Ex: Essencial, Profissional, Enterprise..." />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Preço mensal (R$) *</label>
                        <MoneyInput
                          value={planForm.price}
                          onChange={v => setPlanForm(f => ({ ...f, price: v }))}
                          min={0}
                        />
                      </div>
                      <div className="form-group">
                        <label>Máx. usuários</label>
                        <input type="number" min="0" value={planForm.max_users}
                          onChange={e => setPlanForm(f => ({ ...f, max_users: e.target.value }))} placeholder="30" />
                        <small>Use <strong>0</strong> para ilimitado (Enterprise).</small>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group" style={{ flex:1 }}>
                        <label>Preço anual (R$)</label>
                        <MoneyInput
                          value={planForm.price_annual}
                          onChange={v => setPlanForm(f => ({ ...f, price_annual: v }))}
                          min={0}
                          placeholder={planForm.price ? `R$ ${(parseFloat(planForm.price || 0) * 10).toFixed(2).replace('.', ',')}` : 'R$ 0,00'}
                        />
                        {(() => {
                          const m = parseFloat(planForm.price);
                          const a = parseFloat(planForm.price_annual);
                          if (!(m > 0)) return <small>Deixe vazio para usar o padrão (mensal × 10 = 2 meses grátis).</small>;
                          if (!(a > 0)) return <small>Sem preço anual → a landing vai usar <strong>R$ {(m * 10).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> como fallback (mensal × 10).</small>;
                          const baseline = m * 12;
                          const saving = Math.max(0, baseline - a);
                          const pct = Math.round((saving / baseline) * 100);
                          const months = (saving / m).toFixed(1);
                          return <small>Desconto real: <strong>{pct}%</strong> — economia de <strong>R$ {saving.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> por ano ({months} meses grátis). <em>Este cálculo vai direto para a landing.</em></small>;
                        })()}
                      </div>
                    </div>
                    <div className="info-box" style={{ fontSize:12 }}>
                      💡 As features definidas aqui são <strong>padrão para novos clientes</strong> neste plano.
                      Você pode sobrescrever individualmente em cada cliente.
                    </div>
                  </>
                )}
                {planTab === 'features' && (
                  <FeaturesEditor
                    features={planForm.features}
                    onChange={feats => setPlanForm(f => ({ ...f, features: feats }))}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPlanModal(false)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${planSaving?' btn-loading':''}`} disabled={planSaving}>
                  {planSaving ? 'Salvando...' : planEdit ? 'Salvar Plano' : 'Criar Plano'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── PIX view modal (master) ── */}
      {pixModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPixModal(null)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>PIX — {pixModal.tenant_name}</h3>
              <button className="modal-close" onClick={() => setPixModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign:'center', marginBottom:18 }}>
                <div style={{ fontSize:36, marginBottom:6 }}>💳</div>
                <div style={{ fontSize:22, fontWeight:800 }}>{fmt(pixModal.amount)}</div>
                <div style={{ fontSize:12, color:'var(--gray-400)', marginTop:2 }}>
                  Venc: {fmtDate(pixModal.due_date)}
                </div>
              </div>
              <label>Código PIX</label>
              <div className="pix-code-box" style={{ marginTop:6, fontSize:11, wordBreak:'break-all' }}>{pixModal.pix_code}</div>
              <button className="btn btn-primary" style={{ width:'100%', marginTop:12, justifyContent:'center' }}
                onClick={() => { navigator.clipboard.writeText(pixModal.pix_code); toast.success('Código copiado!'); }}>
                📋 Copiar código PIX
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de review de comprovante */}
      {proofReviewModal && (() => {
        const inv = proofReviewModal;
        const extracted = (() => {
          try { return typeof inv.proof_extracted_data === 'string' ? JSON.parse(inv.proof_extracted_data) : inv.proof_extracted_data; }
          catch { return null; }
        })();
        const flags = (() => {
          try { return typeof inv.proof_flags === 'string' ? JSON.parse(inv.proof_flags) : inv.proof_flags; }
          catch { return []; }
        })();
        const isPdf = inv.proof_mime === 'application/pdf';
        const isImage = inv.proof_mime && inv.proof_mime.startsWith('image/');
        return (
          <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setProofReviewModal(null)}>
            <div className="modal modal-lg">
              <div className="modal-header">
                <h3>📎 Revisar comprovante — Fatura #{inv.id}</h3>
                <button className="modal-close" onClick={() => setProofReviewModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase', marginBottom:4 }}>Cliente</div>
                    <div style={{ fontSize:14, fontWeight:600 }}>{inv.tenant_name}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase', marginBottom:4 }}>Valor esperado</div>
                    <div style={{ fontSize:14, fontWeight:700 }}>{fmt(inv.amount)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase', marginBottom:4 }}>Referência</div>
                    <div style={{ fontSize:13 }}>{inv.reference_month || '—'} · Venc {fmtDate(inv.due_date)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase', marginBottom:4 }}>Comprovante enviado</div>
                    <div style={{ fontSize:13 }}>{fmtDate(inv.proof_submitted_at)}</div>
                  </div>
                </div>

                {/* Dados extraídos */}
                <div className="info-box" style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, marginBottom:6, textTransform:'uppercase' }}>Dados extraídos do arquivo</div>
                  <div style={{ fontSize:13, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div>
                      <strong>Valor:</strong> {extracted?.valor != null ? fmt(extracted.valor) : <em style={{color:'var(--gray-400)'}}>não encontrado</em>}
                      {extracted?.valor != null && Math.abs(extracted.valor - parseFloat(inv.amount)) > 1 && (
                        <span style={{ color:'var(--danger)', marginLeft:6 }}>⚠️ diverge</span>
                      )}
                    </div>
                    <div><strong>Data:</strong> {extracted?.data_iso || <em style={{color:'var(--gray-400)'}}>não encontrada</em>}</div>
                    <div style={{ gridColumn:'span 2' }}><strong>Pagador:</strong> {extracted?.pagador || <em style={{color:'var(--gray-400)'}}>não identificado</em>}</div>
                    <div style={{ gridColumn:'span 2', wordBreak:'break-all' }}><strong>E2E ID:</strong> <code style={{ fontSize:11 }}>{extracted?.e2e_id || 'não encontrado'}</code></div>
                  </div>
                </div>

                {/* Flags / motivos */}
                {flags?.length > 0 && (
                  <div className="warning-box" style={{ fontSize:13, marginBottom:14 }}>
                    <strong>Motivos da revisão:</strong>
                    <ul style={{ margin:'4px 0 0 18px', padding:0 }}>
                      {flags.map((f, i) => <li key={i} style={{ fontSize:12 }}>{f}</li>)}
                    </ul>
                  </div>
                )}

                {/* Preview do arquivo */}
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--gray-500)', marginBottom:6, textTransform:'uppercase' }}>Preview do comprovante ({inv.proof_mime})</div>
                  {isPdf && inv.proof_file && (
                    <embed src={inv.proof_file} type="application/pdf" width="100%" height="400" style={{ border:'1px solid var(--gray-200)', borderRadius:8 }} />
                  )}
                  {isImage && inv.proof_file && (
                    <img src={inv.proof_file} alt="Comprovante" style={{ maxWidth:'100%', maxHeight:400, display:'block', margin:'0 auto', border:'1px solid var(--gray-200)', borderRadius:8 }} />
                  )}
                  {inv.proof_file && (
                    <div style={{ textAlign:'center', marginTop:8 }}>
                      <a href={inv.proof_file} download={`comprovante-fatura-${inv.id}.${isPdf ? 'pdf' : 'png'}`} className="btn btn-sm btn-secondary">⬇️ Baixar</a>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Notas da revisão (opcional)</label>
                  <textarea
                    rows={2}
                    value={proofReviewNotes}
                    onChange={e => setProofReviewNotes(e.target.value)}
                    placeholder="Motivo da decisão, observação pro cliente, etc."
                    disabled={proofReviewActing}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setProofReviewModal(null)} disabled={proofReviewActing}>Cancelar</button>
                <button
                  className={`btn btn-danger${proofReviewActing ? ' btn-loading' : ''}`}
                  onClick={() => handleProofReview('reject')}
                  disabled={proofReviewActing}
                >
                  ✕ Rejeitar comprovante
                </button>
                <button
                  className={`btn btn-success${proofReviewActing ? ' btn-loading' : ''}`}
                  onClick={() => handleProofReview('confirm')}
                  disabled={proofReviewActing}
                >
                  ✓ Confirmar pagamento
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
